// Fetches every Data QA results JSON for a company from S3, newest-first (S3List
// + signed-url GET on bpm-econ). The R QA pipeline writes {data_qa_id}_data_qa.json
// per run; see dataQaContract.ts for the shape.
// TODO(forecasting-self-serve): eng may prefer a lambda endpoint for this load
// (MMM Data QA uses /getMMMDataQALoad) — a swap stays isolated here.
import { useCallback, useEffect, useRef, useState } from 'react';
import { S3List, S3SignedUrlFetch2 } from '../../utils/fetch-utils';
import { DataQaResults, DataQaSeries } from './dataQaContract';
import { DATA_QA_S3_BUCKET, DATA_QA_S3_PREFIX } from './forecasting-constants';

/**
 * Fetches the sibling weekly-series file (schema v2) named by the main file's
 * `series_file`, in the same S3 prefix — so the key is derived from the main key.
 */
export const fetchDataQaSeries = async (
  mainS3Key: string,
  seriesFile: string
): Promise<DataQaSeries> => {
  const seriesKey = mainS3Key.replace(/[^/]+$/, seriesFile);
  const res = await S3SignedUrlFetch2(DATA_QA_S3_BUCKET, seriesKey, 'application/json');
  if (!res.ok) {
    throw new Error(`series fetch failed (${res.status})`);
  }
  return (await res.json()) as DataQaSeries;
};

export interface DataQaRun {
  s3Key: string;
  data: DataQaResults;
  /** UTC instant of `data.generated_at`. */
  generatedAtMs: number;
}

// Time present but no `Z`/±offset. Date-only is excluded: `Date.parse` already
// reads those as UTC, and appending `Z` to one isn't valid ISO.
const TIME_WITHOUT_OFFSET = /T[\d:.]+$/;

/**
 * Reads an offset-less `generated_at` as UTC (the contract's timezone); `Date.parse`
 * alone would treat it as local. The only parse site, so stored/displayed can't disagree.
 */
export const parseGeneratedAt = (generatedAt: string): number =>
  Date.parse(TIME_WITHOUT_OFFSET.test(generatedAt) ? `${generatedAt}Z` : generatedAt);

export type ForecastDataQaState =
  | { status: 'loading' }
  | { status: 'empty' } // no QA run for this client yet (cold start)
  | { status: 'error'; message: string }
  // `data`/`s3Key` are the newest run (= `runs[0]`); `runs` is every parseable run.
  | { status: 'ready'; data: DataQaResults; s3Key: string; runs: DataQaRun[] };

export type ForecastDataQaResult = ForecastDataQaState & {
  /** Re-list S3 and reload the runs — e.g. after the "run finished" email. */
  refetch: () => void;
  /** A refetch() is in flight; the current runs stay shown until it resolves. */
  isRefreshing: boolean;
};

export const useForecastDataQa = (company: string | undefined): ForecastDataQaResult => {
  const [state, setState] = useState<ForecastDataQaState>({ status: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const loadedCompanyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!company) {
      // This early return skips the finally below, so clear the flag here.
      setIsRefreshing(false);
      return undefined;
    }
    // Reset to loading on a company switch (but not a refetch) so the new client
    // can't show or preserve the previous client's runs.
    const isRefetch = company === loadedCompanyRef.current;
    loadedCompanyRef.current = company;
    if (!isRefetch) {
      setState({ status: 'loading' });
    }
    let cancelled = false;
    (async () => {
      try {
        const keys = await S3List({
          bucket: DATA_QA_S3_BUCKET,
          prefix: `${DATA_QA_S3_PREFIX}/${company}`,
          contains: '_data_qa.json'
        });
        // endsWith keeps the match strict (e.g. excludes accidental .bak copies)
        const jsonKeys = keys.filter(k => k.endsWith('_data_qa.json'));
        if (jsonKeys.length === 0) {
          if (!cancelled) {
            // Keep a prior successful load on refetch rather than dropping to empty.
            setState(prev => (prev.status === 'ready' ? prev : { status: 'empty' }));
          }
          return;
        }
        // Run ids are uuids, so recency isn't in the key — fetch every candidate
        // and sort by generated_at (few runs per client). Both schema versions
        // parse (v1 just has no series_file); unparseable/hung ones are skipped.

        // TODO(forecasting-self-serve): every run's full JSON is downloaded up
        // front, so volume grows with run count. Have the pipeline write a
        // manifest (run id + generated_at) so runs list cheaply and each file is
        // fetched only when selected.
        const fetchCandidate = async (s3Key: string) => {
          let timer: number | undefined;
          try {
            const res = await Promise.race([
              S3SignedUrlFetch2(DATA_QA_S3_BUCKET, s3Key, 'application/json'),
              new Promise<never>((_, reject) => {
                timer = window.setTimeout(() => reject(new Error('timeout')), 30_000);
              })
            ]);
            if (!res.ok) return null;
            const data = (await res.json()) as DataQaResults;
            const generatedAtMs = parseGeneratedAt(data?.generated_at ?? '');
            if (!data?.groups?.length || Number.isNaN(generatedAtMs)) return null;
            return { s3Key, data, generatedAtMs };
          } catch {
            return null;
          } finally {
            // Clear the race timer so it doesn't fire into an already-settled race.
            window.clearTimeout(timer);
          }
        };
        const parsed = await Promise.all(jsonKeys.map(fetchCandidate));
        const candidates = parsed
          .filter((p): p is NonNullable<typeof p> => p !== null)
          // Newest first; tie-break on s3Key for deterministic ordering.
          .sort((a, b) => b.generatedAtMs - a.generatedAtMs || a.s3Key.localeCompare(b.s3Key));
        if (cancelled) return;
        if (candidates.length === 0) {
          // Files exist but none parsed — a load failure, not a cold start, so
          // surface an error rather than sending the user back to onboarding
          // (keeping current runs on a refetch).
          setState(prev =>
            prev.status === 'ready'
              ? prev
              : { status: 'error', message: 'Data QA results exist but could not be loaded.' }
          );
        } else {
          setState({
            status: 'ready',
            data: candidates[0].data,
            s3Key: candidates[0].s3Key,
            runs: candidates
          });
        }
      } catch (e) {
        if (!cancelled) {
          // Keep the current runs on a transient refetch failure.
          setState(prev =>
            prev.status === 'ready' ? prev : { status: 'error', message: e.message }
          );
        }
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, reloadNonce]);

  const refetch = useCallback(() => {
    setIsRefreshing(true);
    setReloadNonce(n => n + 1);
  }, []);

  return { ...state, refetch, isRefreshing };
};
